import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { ACTIVITY_SESSION_JOIN_PATH, parseActivitySessionQR, parseEquipmentQR } from 'shared';
import QRScanner from '@/components/QRScanner';
import {
  isAlreadyCheckedIn,
  useCheckInCentro,
  useCurrentCentro,
} from '@/hooks/useCentroVisits';
import { useEquipmentsByCentro } from '@/hooks/useEquipments';
import { useScanEquipment } from '@/hooks/useReservations';
import {
  checkInErrorMessage,
  EQUIPMENT_NOT_IN_CENTRO_MESSAGE,
  scanErrorMessage,
} from '@/lib/errors';

/**
 * Banner shape handed to ReservationsPage via router state after an equipment
 * scan. Mirrors the local `Banner` union on ReservationsPage so it can render
 * the feedback with its existing markup.
 */
export type ScanBanner =
  | { kind: 'success'; message: string }
  | { kind: 'error'; message: string };

/**
 * The green nav FAB routes here. This page is the scan orchestrator: it opens
 * the camera immediately and routes the decoded QR through the existing flows.
 *
 *  - Activity session  → whatever the Centro state, the QR a leader shows on
 *    the first slide hands over to `/join/:token`, which records presence and
 *    (if needed) the Centro check-in. Checked first because it is the one code
 *    that is valid both before and after a Centro check-in.
 *  - No active Centro  → the scan is a Centro check-in. On success we land on
 *    Início (`/`). The participant cannot pick another Centro until they press
 *    "Terminar visita" there, because an active Centro flips this page into
 *    equipment mode.
 *  - Active Centro     → the scan is an equipment toggle (reuses the atomic
 *    `/scan` endpoint: first scan reserves, scanning the same item again ends
 *    the reservation). We then land on Reservas (`/reservations`) and surface
 *    the result banner there.
 */
export default function ScanPage() {
  const navigate = useNavigate();
  const currentQuery = useCurrentCentro();
  const checkIn = useCheckInCentro();
  const scanEquipment = useScanEquipment();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Mirrors the proven Centro/equipment flows (HomePage, ReservationsPage): close
  // (unmount) the scanner before any async work so the full-screen camera overlay
  // is torn down deterministically before we navigate. Otherwise the overlay can
  // outlive the route change and blank out the destination screen.
  const [scannerOpen, setScannerOpen] = useState(true);

  const activeCentro = currentQuery.data ?? null;
  const equipmentsQuery = useEquipmentsByCentro(activeCentro?.digitalCenterId);
  const processing = checkIn.isPending || scanEquipment.isPending;

  const handleDecoded = async (qr: string) => {
    setScannerOpen(false);
    setErrorMessage(null);

    const sessionToken = parseActivitySessionQR(qr);
    if (sessionToken !== null) {
      navigate(`${ACTIVITY_SESSION_JOIN_PATH}${sessionToken}`, { replace: true });
      return;
    }

    if (!activeCentro) {
      // First scan of the visit: check into a Centro, then go to Início.
      try {
        await checkIn.mutateAsync({ qrCode: qr });
        navigate('/', { replace: true });
      } catch (err) {
        if (isAlreadyCheckedIn(err)) {
          // Stale cache said "no Centro" but the participant is in fact checked
          // in elsewhere. The mutation invalidated the centros cache, so Início
          // will reflect the active visit.
          navigate('/', { replace: true });
          return;
        }
        setErrorMessage(checkInErrorMessage(err));
      }
      return;
    }

    // Already checked in: every subsequent scan toggles an equipment reservation.
    const equipmentId = parseEquipmentQR(qr);
    if (equipmentId === null) {
      navigate('/reservations', {
        replace: true,
        state: {
          scanBanner: {
            kind: 'error',
            message: 'QR não reconhecido. Use o QR do equipamento.',
          } satisfies ScanBanner,
        },
      });
      return;
    }

    // PWA-only guard: refuse equipment that doesn't belong to this Centro before
    // hitting the backend. Only enforced once the Centro's equipment list has
    // loaded; on a cold cache we fall through and let the backend validate.
    const known = equipmentsQuery.data?.equipments;
    if (known && !known.some((eq) => eq.id === equipmentId)) {
      navigate('/reservations', {
        replace: true,
        state: {
          scanBanner: {
            kind: 'error',
            message: EQUIPMENT_NOT_IN_CENTRO_MESSAGE,
          } satisfies ScanBanner,
        },
      });
      return;
    }

    try {
      const result = await scanEquipment.mutateAsync({ equipmentId });
      const equipmentName = result.equipment.equipamento;
      const scanBanner: ScanBanner = {
        kind: 'success',
        message:
          result.action === 'started'
            ? `Reserva criada para ${equipmentName}.`
            : `Devolveu o ${equipmentName}.`,
      };
      // A return may be the last thing before leaving: Reservas offers to end
      // the visit too.
      const state = result.action === 'ended' ? { scanBanner, offerLeave: true } : { scanBanner };
      navigate('/reservations', { replace: true, state });
    } catch (err) {
      navigate('/reservations', {
        replace: true,
        state: {
          scanBanner: {
            kind: 'error',
            message: scanErrorMessage(err),
          } satisfies ScanBanner,
        },
      });
    }
  };

  const scannerReady = !currentQuery.isLoading && !processing && !errorMessage;

  return (
    <>
      <Helmet>
        <title>Ler QR | AICD</title>
      </Helmet>

      <div className="space-y-6">
        <header>
          <h1 className="text-2xl font-black text-foreground">Ler QR</h1>
          <p className="mt-1 subtitle text-sm text-muted-foreground">
            {activeCentro
              ? 'Leia o QR de um equipamento para criar ou terminar a reserva, ou o QR de uma atividade.'
              : 'Leia o QR de um Centro Digital ou de uma atividade para começar.'}
          </p>
        </header>

        {(currentQuery.isLoading || processing) && (
          <div
            role="status"
            className="rounded-xl border border-border bg-input-soft p-4 text-sm text-muted-foreground"
          >
            {processing ? 'A processar…' : 'A preparar o leitor…'}
          </div>
        )}

        {errorMessage && (
          <div className="card space-y-4 p-6 text-center">
            <p role="alert" className="text-sm text-busy">
              {errorMessage}
            </p>
            <button
              type="button"
              onClick={() => {
                setErrorMessage(null);
                setScannerOpen(true);
              }}
              className="inline-flex w-full items-center justify-center rounded-xl bg-primary px-4 py-3 text-sm font-bold text-primary-foreground shadow"
            >
              Tentar novamente
            </button>
            <button
              type="button"
              onClick={() => navigate('/', { replace: true })}
              className="inline-flex w-full items-center justify-center rounded-xl border border-border bg-input-soft px-4 py-3 text-sm font-bold text-muted-foreground"
            >
              Voltar ao Início
            </button>
          </div>
        )}
      </div>

      {scannerReady &&
        scannerOpen &&
        (activeCentro ? (
          <QRScanner
            onDecoded={handleDecoded}
            onCancel={() => navigate('/', { replace: true })}
            title="Ler QR do Equipamento"
            promptHint="Toque para iniciar a câmara e ler o código QR do equipamento."
            instructions="Aponte a câmara para o código QR do equipamento para criar a reserva."
          />
        ) : (
          <QRScanner
            onDecoded={handleDecoded}
            onCancel={() => navigate('/', { replace: true })}
            title="Ler QR do Centro"
            promptHint="Toque para iniciar a câmara e ler o código QR do Centro."
            instructions="Aponte a câmara para o código QR do Centro para fazer check-in."
          />
        ))}
    </>
  );
}
